/**
 * useSocial.js — the social graph: follows, blocks, notifications, people search.
 *
 * FIXES FROM THE ORIGINAL
 *
 * "Following and follower counters don't work"
 *   followUser() did two sequential updateDoc calls with arrayUnion and
 *   increment. Any failure between them — offline, tab closed, rules rejection —
 *   left the edge and the counter permanently out of step, and there was no
 *   rollback. Worse, `followedUsers` in the UI was local state seeded from
 *   nothing, so the Follow button reset on every navigation. Now: one
 *   transactional Cloud Function, and the button state is a live subscription
 *   to the actual edge document, so it is always the truth.
 *
 * People search
 *   The original fetched 50 arbitrary users and filtered them in JavaScript.
 *   That returns wrong results past 50 accounts, and hands anyone a bulk export
 *   of your user table. Now it is an indexed prefix query, capped, and honours
 *   each user's `searchable` preference.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection, doc, query, where, orderBy, limit, onSnapshot,
  getDoc, getDocs, updateDoc, setDoc, deleteDoc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { cleanLine } from '../lib/safe';
import { listen } from '../lib/listen';
// Muting has to reach the follower edge the post fan-out reads — see setMuted.
import { setPostAlerts } from './useGroups';

const callToggleFollow = httpsCallable(functions, 'toggleFollow');
const callBlockUser = httpsCallable(functions, 'blockUser');

/* ── Follow state ──────────────────────────────────────────────────────────── */

/**
 * Live "am I following this person". Subscribing to the edge document rather
 * than holding a local array is what makes the button correct across tabs,
 * navigations and reloads.
 */
export function useIsFollowing(myUid, targetUid) {
  const [following, setFollowing] = useState(null);   // null = still loading

  useEffect(() => {
    if (!myUid || !targetUid || myUid === targetUid) {
      setFollowing(false);
      return undefined;
    }
    return listen(
      doc(db, 'users', myUid, 'following', targetUid),
      (snap) => setFollowing(snap.exists()),
      // Same trap as votes and reposts: a failed read here says "not following"
      // and the button offers to follow someone you already follow.
      { onFail: () => setFollowing(false) }
    );
  }, [myUid, targetUid]);

  return following;
}

/**
 * Turn a callable's failure into something the person can act on.
 *
 * The old handler collapsed everything into "Could not update follow status.
 * Please try again", which is advice that does not work for any of the real
 * causes: an unverified email needs a click in an inbox, a missing App Check
 * token needs a deploy, and a rate limit needs waiting. Trying again fixes none
 * of them, so saying "try again" wastes the user's time and hides the fault
 * from whoever has to debug it.
 */
export function followError(err) {
  const code = err?.code ?? '';
  const msg = err?.message ?? '';

  if (code === 'functions/unauthenticated') return 'Sign in again to follow people.';
  if (msg.includes('verify your email')) {
    return 'Confirm your email address first — check your inbox for the link.';
  }
  if (msg.includes('could not be verified as coming from')) {
    // Said to members, so no talk of builds or configuration: what they can
    // actually do is reload, or let the security check through a blocker.
    return 'We couldn’t confirm this came from the Clincordant app. Reload the page, and if you use an ad or script blocker, allow Clincordant.';
  }
  if (code === 'functions/resource-exhausted') return msg || 'Too many requests. Try again shortly.';
  if (code === 'functions/not-found') return 'That account no longer exists.';
  if (msg.includes('not available')) return 'This action is not available.';
  if (code === 'functions/internal' || code === 'functions/unavailable') {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return msg || 'Could not update follow status. Please try again.';
}

export function useFollowActions({ onError }) {
  const [pending, setPending] = useState(new Set());

  const toggleFollow = useCallback(async (targetUid) => {
    if (!targetUid) return;
    setPending((p) => new Set(p).add(targetUid));
    try {
      const res = await callToggleFollow({ targetId: targetUid });
      return res.data?.following;
    } catch (err) {
      console.error('toggleFollow failed', err);
      onError?.(followError(err));
      throw err;
    } finally {
      setPending((p) => {
        const next = new Set(p);
        next.delete(targetUid);
        return next;
      });
    }
  }, [onError]);

  const setBlocked = useCallback(async (targetUid, block) => {
    try {
      await callBlockUser({ targetId: targetUid, block });
    } catch (err) {
      console.error('blockUser failed', err);
      onError?.(followError(err));
      throw err;
    }
  }, [onError]);

  /**
   * Mute — hide someone's posts without telling them.
   *
   * Deliberately a plain client write, unlike block. Blocking severs a
   * relationship in both directions and has to be enforced server-side, so it
   * is a Cloud Function. Muting only ever affects what *you* see, lives in your
   * own subcollection, and is nobody else's business — a function call would be
   * cost and latency for no security benefit.
   *
   * useHiddenAuthors() reads this collection live, so the feed filters the
   * moment this resolves.
   */
  const setMuted = useCallback(async (myUid, targetUid, mute) => {
    if (!myUid || !targetUid) return;
    const ref = doc(db, 'users', myUid, 'muted', targetUid);
    try {
      if (mute) await setDoc(ref, { createdAt: serverTimestamp() });
      else await deleteDoc(ref);

      /**
       * Muting silences their post notifications too.
       *
       * Without this, muting hides someone's posts from the feed and then
       * notifies you about every one of them — which is not a half-working
       * mute, it is a mute that makes things worse. The post fan-out cannot
       * check the mute list (that would be a read per recipient, per post), so
       * the mute has to reach the follower edge the fan-out already reads.
       *
       * Best-effort on purpose: the mute itself has landed, and failing the
       * whole action because the notification flag did not flip would undo the
       * part that worked.
       */
      await setPostAlerts('user', targetUid, !mute).catch((err) => {
        console.warn('Mute did not reach post notifications', err);
      });
    } catch (err) {
      console.error('setMuted failed', err);
      onError?.('Could not update mute status. Please try again.');
      throw err;
    }
  }, [onError]);

  return { toggleFollow, setBlocked, setMuted, isPending: (uid) => pending.has(uid) };
}

/* ── Follower / following lists ────────────────────────────────────────────── */

/**
 * Hydrated list of followers or following.
 *
 * The edge documents hold only IDs, so profiles are fetched in a second pass.
 * Capped at 100 per page — an unbounded fan-out on a popular account is how you
 * accidentally issue 20,000 reads from one page view.
 */
export function useConnectionList(userId, kind /* 'followers' | 'following' */) {
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    setStatus('loading');

    (async () => {
      try {
        const edges = await getDocs(
          query(collection(db, 'users', userId, kind), orderBy('createdAt', 'desc'), limit(100))
        );
        if (cancelled) return;

        const profiles = await Promise.all(
          edges.docs.map(async (edge) => {
            const snap = await getDoc(doc(db, 'users', edge.id)).catch(() => null);
            return snap?.exists() ? { id: snap.id, ...snap.data() } : null;
          })
        );
        if (cancelled) return;

        const list = profiles.filter(Boolean);
        setUsers(list);
        setStatus(list.length ? 'ready' : 'empty');
      } catch (err) {
        if (cancelled) return;
        console.error(`${kind} fetch failed`, err);
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [userId, kind]);

  return { users, status };
}

/* ── Blocked / muted sets ──────────────────────────────────────────────────── */

/**
 * The set of authors to filter out of feeds and comments.
 *
 * This is a UX filter. The security boundary for blocking lives in the rules
 * (profiles, DMs, notifications). See SECURITY.md, "Known limitation: feed-level
 * block enforcement".
 */
export function useHiddenAuthors(uid) {
  const [blocked, setBlocked] = useState(new Set());
  const [muted, setMuted] = useState(new Set());

  useEffect(() => {
    if (!uid) return undefined;
    /**
     * These two matter more than the rest, and they used to swallow their
     * errors into an empty Set with `() => {}`.
     *
     * An empty blocked list is not a neutral default. It means every post from
     * someone this person deliberately blocked reappears in their feed, with no
     * error, no indication, and no way to tell it has happened. For a user who
     * blocked someone because of harassment, a listener losing a race is the
     * difference between the product working and the product failing them.
     *
     * Retried, and the failure is logged rather than discarded.
     */
    const unsubBlocked = listen(
      collection(db, 'users', uid, 'blocked'),
      (snap) => setBlocked(new Set(snap.docs.map((d) => d.id))),
      { onFail: (err) => console.error('Block list unavailable', err) }
    );
    const unsubMuted = listen(
      collection(db, 'users', uid, 'muted'),
      (snap) => setMuted(new Set(snap.docs.map((d) => d.id))),
      { onFail: (err) => console.error('Mute list unavailable', err) }
    );
    return () => { unsubBlocked(); unsubMuted(); };
  }, [uid]);

  const hidden = useMemo(() => new Set([...blocked, ...muted]), [blocked, muted]);

  return { blocked, muted, hidden };
}

/* ── Notifications ─────────────────────────────────────────────────────────── */

export function useNotifications(uid) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);

  // A new account starts its own retry budget.
  useEffect(() => { setAttempt(0); }, [uid]);

  useEffect(() => {
    if (!uid) return undefined;

    let cancelled = false;
    let retry = null;

    const q = query(
      collection(db, 'users', uid, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setStatus(snap.empty ? 'empty' : 'ready');
      },
      (err) => {
        console.error('Notifications subscription failed', err);

        /**
         * A permission error here is usually a race, not a rule.
         *
         * The rule is `allow read: if isOwner(userId)` — it compares the uid in
         * the path against the uid in the auth token. Immediately after a
         * sign-in, or an account switch, those two can disagree for a moment:
         * React re-renders with the new uid and re-subscribes before the SDK
         * has finished swapping the token, so the listener asks for account B's
         * notifications while still holding account A's credentials.
         *
         * What makes that catastrophic rather than momentary is that
         * onSnapshot NEVER retries after a permission error. It reports once and
         * gives up, so a listener that lost a race by fifty milliseconds stays
         * dead for the whole session — the bell shows a stale cached count and
         * the page shows "could not load" until something forces a remount.
         *
         * So: force a token refresh and re-subscribe, backing off, three times.
         * If it is genuinely a rules problem the fourth failure surfaces the
         * error properly and nothing is hidden.
         */
        if (err?.code === 'permission-denied' && attempt < 3 && !cancelled) {
          retry = setTimeout(async () => {
            try { await auth.currentUser?.getIdToken(true); } catch { /* offline */ }
            if (!cancelled) setAttempt((a) => a + 1);
          }, 800 * (attempt + 1));
          return;
        }

        setStatus('error');
      }
    );

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      unsub();
    };
  }, [uid, attempt]);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const markAllRead = useCallback(async () => {
    if (!uid) return;
    const unread = items.filter((n) => !n.read).slice(0, 400);
    if (!unread.length) return;
    // Firestore caps a batch at 500 writes.
    for (let i = 0; i < unread.length; i += 400) {
      const batch = writeBatch(db);
      for (const n of unread.slice(i, i + 400)) {
        batch.update(doc(db, 'users', uid, 'notifications', n.id), {
          read: true,
          readAt: serverTimestamp(),
        });
      }
      await batch.commit().catch(() => {});
    }
  }, [uid, items]);

  const markRead = useCallback(async (notifId) => {
    if (!uid) return;
    await updateDoc(doc(db, 'users', uid, 'notifications', notifId), {
      read: true,
      readAt: serverTimestamp(),
    }).catch(() => {});
  }, [uid]);

  const remove = useCallback(async (notifId) => {
    if (!uid) return;
    await deleteDoc(doc(db, 'users', uid, 'notifications', notifId)).catch(() => {});
  }, [uid]);

  /**
   * Clear everything currently loaded.
   *
   * Bounded by the 50 the listener holds, which is deliberate: "clear all"
   * should empty what the person is looking at, and a sweep of an unbounded
   * collection from a browser is a good way to half-finish and leave the list
   * in a state nobody can explain. If more arrive behind it, pressing it again
   * is honest.
   */
  const clearAll = useCallback(async () => {
    if (!uid || !items.length) return;
    for (let i = 0; i < items.length; i += 400) {
      const batch = writeBatch(db);
      for (const n of items.slice(i, i + 400)) {
        batch.delete(doc(db, 'users', uid, 'notifications', n.id));
      }
      await batch.commit().catch(() => {});
    }
  }, [uid, items]);

  return {
    notifications: items, unreadCount, status,
    markAllRead, markRead, remove, clearAll,
  };
}

/**
 * Mark the single rolled-up notification for a conversation as read.
 *
 * Called when the thread is opened. onMessageSent writes one row per
 * conversation under the fixed id `dm_<convId>` and skips while it is unread,
 * so marking it read here is precisely what re-arms it: the next message that
 * arrives once you have left produces a fresh notification, and messages that
 * arrive while you are still looking are suppressed by presence instead.
 */
export async function clearConversationNotification(uid, convId) {
  if (!uid || !convId) return;
  await updateDoc(doc(db, 'users', uid, 'notifications', `dm_${convId}`), {
    read: true,
    readAt: serverTimestamp(),
  }).catch(() => {});   // absent is the normal case, not an error
}

/* ── People search ─────────────────────────────────────────────────────────── */

/**
 * Indexed prefix search over handles and display names.
 *
 * The '' trick is the standard Firestore prefix bound: it is a very high
 * code point, so `>= "amar"` and `<= "amar"` brackets every string
 * starting with "amar".
 */
export function useUserSearch(term, { myUid, hiddenAuthorIds } = {}) {
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const q = cleanLine(term, 40).toLowerCase().replace(/^@/, '');

    if (q.length < 2) {
      setResults([]);
      setStatus('idle');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    // Debounced: a query per keystroke is both slow and expensive.
    const timer = setTimeout(async () => {
      try {
        const bound = `${q}`;

        const [byHandle, byName, byToken] = await Promise.all([
          getDocs(query(
            collection(db, 'users'),
            where('searchable', '==', true),
            orderBy('usernameLower'),
            where('usernameLower', '>=', q),
            where('usernameLower', '<=', bound),
            limit(8)
          )),
          getDocs(query(
            collection(db, 'users'),
            where('searchable', '==', true),
            orderBy('displayNameLower'),
            where('displayNameLower', '>=', q),
            where('displayNameLower', '<=', bound),
            limit(8)
          )),
          /**
           * Any part of the name, not only the start of the whole string.
           *
           * displayNameLower is assembled as title + first + last, so it reads
           * "mrs sarah jones" — and a prefix query on that only matches someone
           * who thinks to type the honorific first. Nobody does. searchTokens
           * holds each part separately, which is what makes searching "sarah"
           * or "jones" find her.
           *
           * array-contains matches whole tokens only, so this complements the
           * two prefix queries rather than replacing them: partial first name
           * from the range query, exact surname from this one.
           *
           * Tolerated failure: profiles written before searchTokens existed do
           * not have the field, and the composite index may not be built yet.
           * Neither should take the whole search down.
           */
          getDocs(query(
            collection(db, 'users'),
            where('searchable', '==', true),
            where('searchTokens', 'array-contains', q),
            limit(8)
          )).catch((err) => {
            console.warn('Token search unavailable', err);
            return { docs: [] };
          }),
        ]);

        if (cancelled) return;

        const seen = new Set();
        const merged = [];
        for (const snap of [...byHandle.docs, ...byName.docs, ...byToken.docs]) {
          if (seen.has(snap.id)) continue;
          if (snap.id === myUid) continue;
          if (hiddenAuthorIds?.has(snap.id)) continue;
          seen.add(snap.id);
          merged.push({ id: snap.id, ...snap.data() });
        }

        setResults(merged.slice(0, 10));
        setStatus(merged.length ? 'ready' : 'empty');
      } catch (err) {
        if (cancelled) return;
        console.error('User search failed', err);
        setStatus('error');
      }
    }, 250);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [term, myUid, hiddenAuthorIds]);

  return { results, status };
}

/**
 * Resolve a @handle to a uid.
 *
 * Profile URLs are /u/:handle because a URL full of Firebase UIDs is hostile to
 * share and impossible to read, but every other collection keys off the uid.
 * The usernames/{handle} document is the index that bridges them — and its
 * rules allow `get` but deny `list`, so a single lookup works and scraping the
 * whole handle table does not.
 *
 * Status is separate from the id so the page can tell "still looking" apart
 * from "no such person", which need very different screens.
 */
export function useHandleOwner(handle) {
  const [uid, setUid] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | missing | error

  useEffect(() => {
    const key = String(handle || '').trim().toLowerCase();
    if (!key) {
      setUid(null);
      setStatus('missing');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    getDoc(doc(db, 'usernames', key))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setUid(null);
          setStatus('missing');
          return;
        }
        setUid(snap.data().uid);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Handle lookup failed', err);
        setStatus('error');
      });

    return () => { cancelled = true; };
  }, [handle]);

  return { uid, status };
}

/** One profile, live — used by the profile page and the DM header. */
export function useProfile(userId) {
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (!userId) {
      setStatus('missing');
      return undefined;
    }
    setStatus('loading');
    return onSnapshot(
      doc(db, 'users', userId),
      (snap) => {
        if (!snap.exists()) {
          setProfile(null);
          setStatus('missing');
          return;
        }
        setProfile({ id: snap.id, ...snap.data() });
        setStatus('ready');
      },
      (err) => {
        // Permission denied here means a block exists in one direction.
        console.warn('Profile unavailable', err.code);
        setProfile(null);
        setStatus(err.code === 'permission-denied' ? 'blocked' : 'error');
      }
    );
  }, [userId]);

  return { profile, status };
}

/** Resolve @handle → uid, for /u/:handle URLs. */
export async function resolveHandle(handle) {
  const clean = cleanLine(handle, 30).toLowerCase().replace(/^@/, '');
  if (!clean) return null;
  const snap = await getDoc(doc(db, 'usernames', clean)).catch(() => null);
  return snap?.exists() ? snap.data().uid : null;
}
